import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useHistory } from "react-router";

import { PreviewText } from "@highnote/preview-text/src";
import { Space, Track } from "@highnote/server/src/core/entities";
import { sortItemsByItemsOrder } from "@highnote/server/src/core/shared-util";
import { highnote } from "@highnote/server/src/sdk";
import { ReactComponent as CaretDownSVG } from "App/common/icons-v2/carat-down.svg";
import { ReactComponent as CaretRightSVG } from "App/common/icons-v2/carat-right.svg";
import { ReactComponent as FolderHollowSVG } from "App/common/icons-v2/folder-fill.svg";
import { ReactComponent as MusicFillSVG } from "App/common/icons-v2/music-fill.svg";
import { ReactComponent as TrackVersionSVG } from "App/common/icons-v2/waveform.svg";
import { List, ListItem, ListItemButton, ListSubheader } from "App/common/List";
import { PREVIEW_ICON_SIZE, PreviewIcon } from "App/common/PreviewIcon";
import { useConfirmation } from "App/common/useConfirmation";
import { useViewport } from "App/common/useViewport";
import { useAuth } from "App/components/Auth";
import { EntityRowConfig } from "App/components/EntityTable";
import { Version } from "App/components/EntityTable/config";
import { trackDeeplinkConfirmPayload } from "App/components/EntityTable/ExpandableSpace/ExpandableSpaceRow";
import { useTrackChildItemRows } from "App/components/EntityTable/ExpandableTrack/ExpandableTrackRow";
import {
  ALT_TRACK_VERSION_ACTIONS,
  TrackVersionActions,
} from "App/components/TrackEditor/TrackVersions/TrackVersionActions";
import { DefaultTrackActions } from "App/components/useEntities/useLibraryTracks/TrackActions";
import { useTopLevelSpaces } from "App/components/useEntities/useTopLevelSpaces";
import { useUser } from "App/components/useEntities/useUser";
import { useFiles } from "App/components/useFiles";
import { TrackEditorProvider } from "App/components/useTrackEditor";
import { sortRowByCreatedAtAscending } from "App/components/util";
import { Button, BUTTON_THEME } from "App/core/Button";
import { withMobileDeeplinkRoot } from "App/modules/urls";
import { isTouchDevice } from "App/modules/utils";
import { useUrlContext } from "App/routes/Main/useUrlContext";
import { useChildEntityWatchers } from "App/store/helpers/useChildEntityWatchers";
import {
  useGlobalSpaceCached,
  useGlobalSpaces,
} from "App/store/spaces/useGlobalSpaces";
import { useGlobalTracks } from "App/store/tracks/useGlobalTracks";
import { WatcherComponentId } from "App/store/watchers/useWatcherV2";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { SpaceStub } from "App/components/useEntities/spaceStub";
import { LazySpaceActions } from "App/components/useEntities/LazySpaceActions";
import { useSidebarContext } from "..";
import { MuteToggle } from "./MuteToggle";
import { PinToggle } from "./PinToggle/PinToggle";
import "./SpacePicker.scss";
import { Skeleton } from "App/common/Skeleton/Skeleton";
import { asViewingSpaceId, ViewingSpaceId } from "App/modules/viewingSpace";
import { RenderableTrack } from "App/store/tracks/buildSpaceTrackTile";
import { useGlobalSpaceTracks } from "App/store/spaceTracks/useGlobalSpaceTracks";

// this is to make the column to have 22px width and 4px gutter as per figma mock
const NESTED_ENTITY_COLUMN_WIDTH = 26;

const calculateChildIndentation = (indentation: number) => {
  return NESTED_ENTITY_COLUMN_WIDTH + indentation * NESTED_ENTITY_COLUMN_WIDTH;
};

const EmptyListItem = ({
  indentation = 0,
  style,
}: {
  indentation?: number;
  style?: CSSProperties;
}) => {
  return (
    <ListItem
      className="empty-list-item"
      data-indentation={indentation}
      style={style}
    >
      <div className="row">Empty</div>
    </ListItem>
  );
};

const SkeletonListItem = ({
  indentation = 0,
  rows = 1,
}: {
  indentation?: number;
  rows?: number;
}) => {
  return (
    <div
      className="sidebar-child-row-skeleton-container"
      style={{
        paddingLeft: `${calculateChildIndentation(indentation)}px`,
      }}
    >
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={`skeleton-${index}`} height={24} />
      ))}
    </div>
  );
};

type BaseSpaceTrackItemProps = {
  indentation: number;
  isExpandable?: boolean;
  /** The sidebar space this item is listed under — its viewing space. */
  spaceId?: ViewingSpaceId;
};

type TrackSpaceTrackItemProps = BaseSpaceTrackItemProps & {
  type: "track";
  entity: RenderableTrack;
};

type TrackVersionTrackItemProps = BaseSpaceTrackItemProps & {
  type: "track-version";
  entity: Version;
  track: Track;
};

type SpaceTrackItemProps =
  | TrackSpaceTrackItemProps
  | TrackVersionTrackItemProps;

const SpaceTrackItem = ({
  type,
  entity,
  indentation,
  isExpandable,
  spaceId,
  ...props
}: SpaceTrackItemProps) => {
  const history = useHistory();
  const { user } = useUser();
  const { shareKey } = useAuth();
  const { confirm, renderConfirmation } = useConfirmation();
  const { trackId, versionIdPath } = useUrlContext();
  const { closeSidebar } = useSidebarContext();
  const { isMobile: isMobileViewport } = useViewport();
  const isMobile = isMobileViewport || isTouchDevice();

  const isTrackVersion = type === "track-version";
  const isTrack = type === "track";

  const [isExpanded, setExpanded] = useState<boolean>(
    isExpandable && isTrack && trackId && entity?.id === trackId,
  );

  // Stay in the sidebar space this item is listed under. A borrowed
  // track's `spaceId` is its ORIGIN — a space the viewer often cannot read;
  // a row-only tile has no `spaceId` at all.
  const navigateToTrackPage = () => {
    if (isTrackVersion) {
      const { track } = props as TrackVersionTrackItemProps;
      history.push(
        `/space/${spaceId ?? track.spaceId}/${track.id}/${entity.id}`,
      );
    } else if (isTrack) {
      const track = entity as RenderableTrack;
      history.push(`/space/${spaceId ?? track.spaceId}/${track.id}`);
    }
    closeSidebar();
  };

  const isSelected =
    !isMobile && isTrackVersion
      ? versionIdPath === entity.id
      : !isMobile && trackId === entity?.id && !versionIdPath;

  return (
    <>
      <ListItemButton
        className={classNames("space-track-list-item", {
          "track-version": isTrackVersion,
        })}
        selected={isSelected}
        style={
          indentation > 0
            ? {
                paddingLeft: `${calculateChildIndentation(indentation)}px`,
              }
            : undefined
        }
        onClick={() => {
          if (user?.isMobileUser && isMobile) {
            return confirm(trackDeeplinkConfirmPayload)
              .then(() => {
                const path = isTrackVersion
                  ? `${(props as TrackVersionTrackItemProps).track.spaceId}/${
                      (props as TrackVersionTrackItemProps).track.id
                    }/${entity.id}`
                  : `${(entity as Track).spaceId}/${(entity as Track).id}`;
                window.location.href = withMobileDeeplinkRoot(
                  `root/library/${path}`,
                  shareKey,
                );
              })
              .catch(navigateToTrackPage);
          }
          navigateToTrackPage();
        }}
      >
        <div className="row">
          {isExpandable && isTrack && (
            <Button
              theme={BUTTON_THEME.ICON}
              type="button"
              className="expander"
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((prev) => !prev);
              }}
            >
              {isExpanded ? <CaretDownSVG /> : <CaretRightSVG />}
            </Button>
          )}
          <div
            className={classNames("column", {
              "track-version-icon": isTrackVersion,
              "track-icon": !isTrackVersion,
            })}
            data-id="icon"
          >
            {isTrackVersion ? <TrackVersionSVG /> : <MusicFillSVG />}
          </div>
          <div className="column" data-id="name">
            <PreviewText>
              {isTrackVersion
                ? (entity as Version).name
                : (entity as Track)?.title}
            </PreviewText>
          </div>
          <div className="column actions" data-id="actions">
            {isTrackVersion ? (
              <TrackVersionActions
                track={
                  type === "track-version"
                    ? (props as TrackVersionTrackItemProps).track
                    : undefined
                }
                version={entity as Version}
                actions={ALT_TRACK_VERSION_ACTIONS}
                actionButtonProps={{
                  size: "xs",
                }}
              />
            ) : (
              <DefaultTrackActions
                track={entity as Track}
                viewingSpaceId={spaceId}
                buttonProps={{
                  size: "xs",
                }}
              />
            )}
          </div>
        </div>
        {renderConfirmation}
      </ListItemButton>
      {isExpandable && isTrack && isExpanded && (
        <ExpandableTrackList
          track={entity as Track}
          spaceId={spaceId}
          indentation={indentation + 1}
        />
      )}
    </>
  );
};

const ExpandableTrackList = ({
  track,
  spaceId,
  indentation = 0,
}: {
  track?: Track;
  spaceId?: ViewingSpaceId;
  indentation?: number;
}) => {
  const { trackVersionsLoading, sortedTrackVersions } = useTrackChildItemRows(
    track,
    spaceId,
  );

  const childItemRows = useMemo(() => {
    const childTrackVersions =
      sortedTrackVersions.map((version) => {
        return {
          id: version.id,
          entity: version,
          component: (
            <SpaceTrackItem
              key={`${track?.id ? `${track.id}-` : ""}version-${version.id}`}
              type="track-version"
              track={track}
              entity={version.entity as Version}
              indentation={indentation}
            />
          ),
        };
      }) || [];

    return childTrackVersions;
  }, [sortedTrackVersions]);

  return (
    <List
      className="expandable-space-list"
      data-cypress-id="expandable-space-list"
    >
      {trackVersionsLoading && childItemRows.length === 0 && (
        <SkeletonListItem indentation={indentation} rows={1} />
      )}
      {childItemRows.length === 0 && !trackVersionsLoading ? (
        <EmptyListItem
          indentation={indentation}
          style={
            indentation > 0
              ? {
                  paddingLeft: `${calculateChildIndentation(indentation)}px`,
                }
              : undefined
          }
        />
      ) : (
        childItemRows.map((spaceItem) => {
          return spaceItem.component;
        })
      )}
    </List>
  );
};

const ExpandableSpaceItem = ({
  space,
  indentation = 0,
  artworkUrl,
  componentId,
}: {
  /**
   * Under the new read model a root row is a display projection (a partial
   * space built from the member document), not a space document. This
   * component's own markup reads only `id`, `name`, and `artworkFile`, and
   * the parent resolves `artworkUrl` from
   * `spacesArtworkUrls.get(currentSpace.artworkFile)`. The projection
   * carries all of these, so nothing moves here. Its children are a
   * different matter; see the three call sites below.
   */
  space: SpaceStub;
  componentId: WatcherComponentId;
  indentation?: number;
  artworkUrl?: string;
}) => {
  const history = useHistory();
  const { spaceId, trackId } = useUrlContext();
  const { closeSidebar } = useSidebarContext();
  const [isExpanded, setExpanded] = useState<boolean>(
    spaceId && space?.id === spaceId,
  );
  const { isMobile } = useViewport();

  return (
    <>
      <ListItemButton
        className="expandable-space-item"
        selected={!isMobile && spaceId === space.id && !trackId}
        style={
          indentation > 0
            ? {
                paddingLeft: `${calculateChildIndentation(indentation)}px`,
              }
            : undefined
        }
        data-cypress-id="expandable-space-item"
        data-cypress-space-id={space.id}
        onClick={() => {
          history.push(`/space/${space.id}`);
          closeSidebar();
        }}
      >
        <div className="row">
          <Button
            theme={BUTTON_THEME.ICON}
            type="button"
            className="expander"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded(!isExpanded);
            }}
          >
            {isExpanded ? <CaretDownSVG /> : <CaretRightSVG />}
          </Button>
          {space.artworkFile ? (
            <div className="column space-artwork" data-id="icon">
              <PreviewIcon
                size={PREVIEW_ICON_SIZE.SMALL}
                src={artworkUrl}
                fallback={<FolderHollowSVG />}
              />
            </div>
          ) : (
            <div className={classNames("column space-icon")} data-id="icon">
              <FolderHollowSVG />
            </div>
          )}
          <div className="column" data-id="name">
            <PreviewText>{space.name}</PreviewText>
          </div>

          <div className="column" data-id="mute">
            <MuteToggle entity={space} />
          </div>

          <div className="column" data-id="pinned">
            <PinToggle entity={space} className="PinToggle" />
          </div>

          <div className="column actions" data-id="actions">
            {/* Do not use `DefaultSpaceActions` directly here. The obvious
                fix is to widen its prop, and that fix produces a silently
                wrong menu. See the header comment of
                `LazySpaceActions`. */}
            <LazySpaceActions
              space={space}
              componentId={componentId}
              buttonProps={{
                size: "xs",
              }}
            />
          </div>
        </div>
      </ListItemButton>
      {isExpanded && (
        <ExpandableSpaceListItem
          space={space}
          indentation={indentation + 1}
          isExpanded={isExpanded}
          componentId={componentId}
        />
      )}
    </>
  );
};

const ExpandableSpaceListItem = ({
  space,
  indentation = 0,
  isExpanded,
  componentId,
}: {
  space?: SpaceStub;
  indentation?: number;
  isExpanded?: boolean;
  componentId: WatcherComponentId;
}) => {
  const { topLevelSpaces, topLevelSpacesLoading } = useTopLevelSpaces();
  const { membersV2Enabled } = useMembershipFlags();
  const { globalSpaces, globalSpacesWatchers } = useGlobalSpaces();
  const { globalTracks } = useGlobalTracks();
  const { getDownloadUrl } = useFiles();
  const [spacesArtworkUrls, setSpacesArtworkUrls] = useState(
    new Map<string, string>(),
  );
  const [spacesArtworkUrlsArray, setSpacesArtworkUrlsArray] = useState<
    Set<string>
  >(new Set());

  /*
   * `itemsOrder` is an unbounded array, so the display projection must not
   * carry it. Without this fetch, an expanded projected folder silently
   * loses its manual child order and falls back to `createdAt` ascending:
   * a visible reorder of every user's sidebar folders, with no error and
   * nothing logged. So the folder fetches its full space document at the
   * moment it expands. The cost is bounded by clicks, not by root count,
   * and it is free in listener terms: the effect below attaches child
   * watchers for this same space on this same render.
   */
  const { space: fetchedSpace } = useGlobalSpaceCached({
    id: space?.id,
    componentId,
    enabled: isExpanded && membersV2Enabled,
  });
  const hydratedSpace = membersV2Enabled ? fetchedSpace : (space as Space);

  /*
   * `hydratedSpace` is a space document or nothing, never a stub, so an
   * absent `itemsOrder` here means the document has not arrived yet.
   *
   * The short gap is deliberate; do not "fix" it with a spinner. Between
   * the expand and the document's arrival, `itemsOrder` is `[]`, so
   * children render in `createdAt` order for one frame and then re-order.
   * A spinner replaces a one-frame reorder with a visible loading state on
   * the app's most-used control.
   */
  const itemsOrder = hydratedSpace?.itemsOrder || [];

  // Fetches all child spaces and tracks of the space
  const { manageGlobalChildEntityWatchers, getChildTracks } =
    useChildEntityWatchers();
  const { globalSpaceTracks } = useGlobalSpaceTracks();

  useEffect(() => {
    if (space?.id && isExpanded) {
      const watcher = manageGlobalChildEntityWatchers({
        spaceId: space.id,
        componentId,
      });
      watcher.attach();
      return () => {
        watcher.detach();
      };
    }
  }, [space?.id, isExpanded, manageGlobalChildEntityWatchers]);

  useEffect(() => {
    const fetchArtworkUrls = async () => {
      if (spacesArtworkUrlsArray.size === 0) return;

      const fileIds = Array.from(spacesArtworkUrlsArray);
      const files = await highnote.getFiles({ ids: fileIds });

      const mappedUrls = new Map<string, string>();
      for (const file of files) {
        const url = await getDownloadUrl(file);
        if (url) {
          mappedUrls.set(file.id, url);
        }
      }
      setSpacesArtworkUrls(mappedUrls);
    };

    fetchArtworkUrls();
  }, [spacesArtworkUrlsArray, getDownloadUrl]);

  // Update artwork file IDs in state
  const updateArtworkFileIds = (artworkFileId: string) => {
    setSpacesArtworkUrlsArray((prev) => {
      if (!prev.has(artworkFileId)) {
        const newSet = new Set(prev);
        newSet.add(artworkFileId);
        return newSet;
      }
      return prev;
    });
  };

  const spaceTracks = useMemo(() => {
    // this means that this component is being rendered at the top level,
    // so we are hiding anything that is not a space.
    if (!space || !globalTracks?.childTracks?.get(space.id)) return [];
    // Tiles, not raw ids: `getChildTracks` resolves a borrowed track whose
    // canonical doc was never fetched from its space-tracks-v1 row, so a
    // borrowing-only space lists its tracks here just as it does on its
    // page (filtering to `globalTracks.tracks` made it read "Empty").
    return getChildTracks(space.id).map((tile) => {
      return {
        id: tile.id,
        entity: tile,
        component:
          (tile.versionFilesV2 || []).length > 1 ? (
            <SpaceTrackItem
              key={`${space.id}-expandable-track-${tile.id}`}
              type="track"
              indentation={indentation}
              entity={tile}
              spaceId={asViewingSpaceId(space.id)}
              isExpandable
            />
          ) : (
            <SpaceTrackItem
              key={`${space.id}-track-${tile.id}`}
              type="track"
              entity={tile}
              spaceId={asViewingSpaceId(space.id)}
              indentation={indentation}
            />
          ),
      };
    });
  }, [
    space,
    globalTracks.childTracks,
    globalTracks.tracks,
    globalSpaceTracks.spaceTrackRows,
  ]);

  const childSpaces = useMemo(() => {
    const processSpaces = (spaces: Space[]) =>
      spaces.map((currentSpace) => {
        if (currentSpace.artworkFile) {
          updateArtworkFileIds(currentSpace.artworkFile);
        }
        return {
          id: currentSpace.id,
          entity: currentSpace,
          component: (
            <ExpandableSpaceItem
              key={currentSpace.id}
              space={currentSpace}
              indentation={indentation}
              artworkUrl={spacesArtworkUrls.get(currentSpace.artworkFile)}
              componentId={componentId}
            />
          ),
        };
      });

    if (!space) {
      /*
       * Both queries return archived roots, and both filter them here,
       * after the fact. So the set of 100 rendered roots is identical, if
       * the projection carries a fresh `isArchived`. `isArchived` is the
       * projection field that changes most often, so it is the one most
       * likely to lag its write for a member. The failure to look for in a
       * manual test is an archived space that reappears in the sidebar,
       * not one that disappears.
       */
      const filteredTopLevelSpaces = topLevelSpaces.filter(
        (currentSpace) => !currentSpace.isArchived,
      );
      return processSpaces(filteredTopLevelSpaces);
    }

    const currentSpaces = globalSpaces?.childSpaces?.get(space?.id) || [];
    const filteredChildSpaces = currentSpaces
      .map((id) => globalSpaces.spaces.get(id))
      .filter((currentSpace) => currentSpace && !currentSpace.isArchived);

    return processSpaces(filteredChildSpaces as Space[]);
  }, [
    space,
    topLevelSpaces,
    globalSpaces.childSpaces,
    globalSpaces.spaces,
    spacesArtworkUrls,
    indentation,
  ]);

  const spaceItems = useMemo(() => {
    if (!space) {
      return childSpaces;
    }
    const allItems = [...spaceTracks, ...childSpaces];
    const { orderedItems, unOrderedItems } = sortItemsByItemsOrder(
      itemsOrder,
      allItems,
    );
    unOrderedItems.sort((aRow, bRow) => {
      return sortRowByCreatedAtAscending(
        aRow as unknown as EntityRowConfig,
        bRow as unknown as EntityRowConfig,
      );
    });
    return [...orderedItems, ...unOrderedItems];
  }, [itemsOrder, spaceTracks, childSpaces]);

  const childWatcherLoading = globalSpacesWatchers
    .get(space?.id)
    ?.loading?.get(componentId);
  const childWatcherError = globalSpacesWatchers.get(space?.id)?.error;

  /*
   * Root mode (`space === undefined`) has no key into the child-watcher
   * map, so `childWatcherLoading` is permanently `undefined` there, and
   * the list renders the literal string "Empty" while the roots still
   * load. Today that shows for a brief moment. Under the member-document
   * query it makes "the collection-group query was denied" and "the query
   * has not answered yet" render identically, which is the silent empty
   * sidebar failure mode.
   *
   * The fix is gated on the flag: with the flag off, `isLoading` in root
   * mode evaluates to `false`, the same falsy value that `undefined`
   * produced. So the off path is byte-identical, and the brief "Empty"
   * moment stays.
   */
  const isLoading = space
    ? childWatcherLoading
    : membersV2Enabled && topLevelSpacesLoading;

  return (
    <List
      className="expandable-space-list"
      data-cypress-id="expandable-space-list"
    >
      {isLoading && spaceItems.length === 0 && (
        <SkeletonListItem indentation={indentation} rows={1} />
      )}
      {spaceItems.map((spaceItem) => {
        return spaceItem.component;
      })}
      {spaceItems.length === 0 && !isLoading && !childWatcherError && (
        <EmptyListItem
          indentation={indentation}
          style={
            indentation > 0
              ? {
                  paddingLeft: `${calculateChildIndentation(indentation)}px`,
                }
              : undefined
          }
        />
      )}
      {/* Today a denied child watcher renders as an empty folder, forever,
          with nothing logged: `useWatcherV2` already stores this error, and
          nothing reads it. This row is gated on the flag because a visible
          failure on the app's highest-risk list is a UX change, not a
          migration change. The Sentry report ships unflagged, in an earlier
          phase. */}
      {membersV2Enabled && childWatcherError && (
        <ListItem className="empty-list-item" data-indentation={indentation}>
          <div className="row">Couldn&apos;t load items</div>
        </ListItem>
      )}
    </List>
  );
};

const PinnedSpacesList = () => {
  const { user } = useAuth();
  const pinnedSpaces = user?.pinnedSpaces;
  const { getDownloadUrl } = useFiles();
  const [isLoadingSpaces, setIsLoadingSpaces] = useState(false);
  const [spacesArtworkUrls, setSpacesArtworkUrls] = useState(
    new Map<string, string>(),
  );
  const { globalSpaces, manageGlobalSingleSpaceWatcher } = useGlobalSpaces();

  const globalPinnedSpaces = useMemo(
    () =>
      (pinnedSpaces ?? []).reduce<Space[]>((acc, id) => {
        const space = globalSpaces.spaces.get(id);
        if (space) acc.push(space);
        return acc;
      }, []),
    [pinnedSpaces, globalSpaces.spaces],
  );

  // Derive artwork file IDs from spaces
  const artworkFileIds = useMemo(
    () => globalPinnedSpaces.map((space) => space.artworkFile).filter(Boolean),
    [globalPinnedSpaces],
  );

  // Fetch artwork URLs when file IDs change
  useEffect(() => {
    if (artworkFileIds.length === 0) return;

    const fetchArtworkUrls = async () => {
      const files = await highnote.getFiles({ ids: artworkFileIds });
      const mappedUrls = new Map<string, string>();

      await Promise.all(
        files.map(async (file) => {
          const url = await getDownloadUrl(file);
          if (url) mappedUrls.set(file.id, url);
        }),
      );

      setSpacesArtworkUrls(mappedUrls);
    };

    fetchArtworkUrls();
  }, [artworkFileIds, getDownloadUrl]);

  // Fetch missing spaces
  useEffect(() => {
    let isMounted = true;
    const spacesToFetch = pinnedSpaces?.filter(
      (id) => !globalPinnedSpaces.some((space) => space.id === id),
    );

    if (!spacesToFetch?.length) {
      if (isMounted) {
        setIsLoadingSpaces(false);
        return;
      }
    }

    if (isMounted) {
      setIsLoadingSpaces(true);
    }

    // Use Promise.all to handle all space fetches in parallel
    // TODO(GlobalSpace): Find a way to fetch in one batch/transaction instead of multiple separate requests
    Promise.all(
      spacesToFetch.map(
        (spaceId) =>
          new Promise((resolve) =>
            manageGlobalSingleSpaceWatcher({
              action: "attach",
              spaceId,
              componentId: "space-picker-pinned-space",
              onSpaceAdd: resolve,
            }),
          ),
      ),
    ).finally(() => {
      if (isMounted) {
        setIsLoadingSpaces(false);
      }
    });

    return () => {
      isMounted = false;
      setIsLoadingSpaces(false);
      // Clean up watchers on unmount or when pinnedSpaces changes
      spacesToFetch.forEach((spaceId) =>
        manageGlobalSingleSpaceWatcher({
          action: "detach",
          spaceId,
          componentId: "space-picker-pinned-space",
        }),
      );
    };
  }, [pinnedSpaces, globalPinnedSpaces]);

  return (
    <>
      {isLoadingSpaces && <SkeletonListItem indentation={0} rows={1} />}
      {globalPinnedSpaces.map((space) => (
        <ExpandableSpaceItem
          key={space.id}
          space={space}
          indentation={0}
          artworkUrl={spacesArtworkUrls.get(space.artworkFile)}
          componentId="space-picker-pinned-space"
        />
      ))}
    </>
  );
};

export const SpacePicker = () => {
  return (
    <div className="space-picker" data-cypress-id="space-picker">
      <span className="spaces-header">Spaces</span>

      <div className="inner">
        <List className="space-list">
          <ListSubheader className="sidebar-spaces-list-header">
            Pinned
          </ListSubheader>
          <TrackEditorProvider>
            <PinnedSpacesList />
          </TrackEditorProvider>
        </List>

        <br />
      </div>

      <div className="inner">
        <List className="space-list">
          <ListSubheader className="sidebar-spaces-list-header">
            All
          </ListSubheader>
          <TrackEditorProvider>
            <ExpandableSpaceListItem componentId="space-picker-all-spaces" />
          </TrackEditorProvider>
        </List>

        <br />
      </div>
    </div>
  );
};
